import { ref } from 'vue'
import { defineStore } from 'pinia'
import i18n from '@/config/i18n.config'

import TravelHiringAnswers from '@/modules/travel/hiring/models/APP/TravelHiringAnswers'
import ENDPOINTS from '@/modules/core/constants/apiEndpoints'
import TravelConfigurations from '@/modules/travel/common/models/APP/TravelConfigurations'
import TravelInsuranceProcessStepEnum from '@/modules/travel/hiring/models/APP/TravelInsuranceProcessStepEnum'
import {
  ErrorDisplayTypeEnum,
  InsuranceProcessStatusEnum,
  UserRoleEnum
} from '@/modules/core/models/enums'
import MiddlewareService from '@/modules/core/services/MiddlewareService'
import ServiceError from '@/modules/core/models/errors/ServiceError'
import ModalButton from '@/modules/core/models/modalMessage/ModalButton'
import Message from '@/modules/core/models/modalMessage/Message'
import { useErrorManagerComposable } from '@/modules/core/composables/errorManagerComposable'
import { useToastStore } from '@/modules/core/stores/toastStore'
import { useLoadingStore } from '@/modules/core/stores/loadingStore'
import { useCommonStore } from '@/modules/core/stores/commonStore'
import { useMessageStore } from '@/modules/core/stores/messageStore'
import { useSupportModalStore } from '@/modules/core/stores/supportModalStore'
import DXPService from '@/modules/core/services/DXPService'
import TravelCommonMapper from '@/modules/travel/common/mappers'
import TravelMapper from '@/modules/travel/hiring/mappers'
import ProductId from '@/modules/common/common/models/DXP/ProductId'
import type DXPProduct from '@/modules/common/common/models/DXP/Product'
import CustomError from '@/modules/core/models/errors/CustomError'
import type TravelInsurance from '../../common/models/APP/TravelInsurance'
import type RaterPricing from '@/modules/common/common/models/MID/RaterPricing'
import { checkRecoveredQuotationBranch } from '@/modules/core/utils/BranchUtils'
import GetProductResponse from '@/modules/common/common/models/DXP/responses/GetProductResponse'
import Insured from '@/modules/common/common/models/APP/Insured'
import TravelHiringSummary from '../models/APP/TravelHiringSummary'
import type Summary from '@/modules/common/hiring/models/APP/Summary'
import type FormPage from '@/modules/common/common/models/APP/FormPage'
import { usePagesBuilder } from '@/modules/core/composables/usePagesBuilderComposable'
import PAGES from '@/modules/travel/hiring/constants/pages'
import { useNavigation } from '@/modules/core/composables/navigationComposable'
import CommonRequestsMapper from '@/modules/common/common/mappers/index'
import { GetPaymentUrlResponse } from '@/modules/common/common/models/MID/responses/GetPaymentUrlResponse'
import ProcessStatus from '@/modules/common/hiring/models/APP/ProcessStatus'
import GTMPageType from '@/modules/core/models/GTMPageType'
import { useGTMStore } from '@/modules/core/stores/gtmStore'

export const useTravelStore = defineStore('travel', () => {
  const errorManager = useErrorManagerComposable()
  const toastStore = useToastStore()
  const loadingStore = useLoadingStore()
  const commonStore = useCommonStore()
  const messageStore = useMessageStore()
  const gtmStore = useGTMStore()
  const supportModalStore = useSupportModalStore()
  const pagesBuilder = usePagesBuilder()
  const navigation = useNavigation()

  const { t } = i18n.global

  const globalConfig = ref(new TravelConfigurations())
  const hiringAnswers = ref(new TravelHiringAnswers())

  function getStepsBeforeSaveAndExit() {
    const stepCode = hiringAnswers.value.processStatus?.stepCode ?? 0
    const steps = []

    steps.push({
      // insurance data
      label: t('copy_save-and-exit-modal_step-insurance'),
      check: true
    })

    steps.push({
      // document step
      label: t('copy_save-and-exit-modal_step-document'),
      noOverride: true,
      check:
        stepCode >= TravelInsuranceProcessStepEnum.DOCUMENTS &&
        !!hiringAnswers.value.holder.documentDetails.documentNumber
    })

    steps.push({
      // policy holder step
      label: t(
        hiringAnswers.value.holder.role === UserRoleEnum.HOLDER_INSURED
          ? 'copy_save-and-exit-modal_step-holder-TA'
          : 'copy_save-and-exit-modal_step-holder-TN'
      ),
      noOverride: true,
      check:
        stepCode >= TravelInsuranceProcessStepEnum.POLICY_HOLDER_SECOND &&
        !!hiringAnswers.value.holder.userDetails.email &&
        (hiringAnswers.value.holder.preContractualDocumentChecked ||
          stepCode > TravelInsuranceProcessStepEnum.POLICY_HOLDER_SECOND)
    })

    hiringAnswers.value.insureds.forEach((insured, i) => {
      if (insured.role === UserRoleEnum.INSURED) {
        // insured steps
        steps.push({
          label: t('copy_save-and-exit-modal_step-insured', {
            n: hiringAnswers.value.holder.role === UserRoleEnum.HOLDER_NOT_INSURED ? i + 1 : i
          }),
          noOverride: true,
          check: stepCode >= TravelInsuranceProcessStepEnum.INSUREDS && !!insured.userDetails.email
        })
      }
    })

    return steps
  }

  function getBranchConfiguration() {
    return globalConfig.value
  }

  function _getCurrentInsured(insuredIndex?: number | string) {
    if (insuredIndex === undefined) return null

    if (insuredIndex === 0 || insuredIndex === 'holder') return hiringAnswers.value.holder

    if (hiringAnswers.value.holder.role === UserRoleEnum.HOLDER_NOT_INSURED) {
      return hiringAnswers.value.insureds[(insuredIndex as number) - 1]
    }

    return hiringAnswers.value.insureds[insuredIndex as number]
  }

  function setCurrentInsuredAnswers(answers: Partial<Insured>, insuredIndex: number) {
    if (
      insuredIndex === 0 &&
      hiringAnswers.value.insureds[0].role === UserRoleEnum.HOLDER_INSURED
    ) {
      hiringAnswers.value.updateHolderAttributes(answers)
      return
    }
    hiringAnswers.value.updateInsuredAttributes(insuredIndex, answers)
  }

  function setGloballyAnswers(answers: Partial<TravelHiringAnswers>) {
    if (answers.holder) {
      hiringAnswers.value.updateHolderAttributes(answers.holder)
      return
    }
    hiringAnswers.value.updateAttributes(answers as any)
  }

  function getHiringAnswers(insuredIndex?: number | string): TravelHiringAnswers {
    const currentInsured = _getCurrentInsured(insuredIndex)
    const insured = currentInsured as Insured
    const answers = hiringAnswers.value as TravelHiringAnswers
    answers.currentInsured = insured ? new Insured(insured) : null

    return answers
  }

  //** PRIVATE SUPPORT METHODS */
  async function _fetchDXPInsurances(products: ProductId[]): Promise<DXPProduct[]> {
    const endpoint = ENDPOINTS.DXP_GET_PRODUCT
    const request = TravelCommonMapper.GetProductsMapper.toRequest(products)
    const { error, data } = await DXPService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    return data.map((d: any) => new GetProductResponse(d).product)
  }

  function _loadCompleteSheetProducts(
    DXPProducts: DXPProduct[],
    MIDProducts: RaterPricing[]
  ): TravelInsurance[] {
    const products: TravelInsurance[] = []
    DXPProducts.forEach((DXPProduct) => {
      const MIDProduct = MIDProducts.find((p) => {
        return String(DXPProduct.productId) === String(p.productCode)
      })

      if (MIDProduct) {
        const insurance = TravelCommonMapper.GetProductsMapper.fromResponse(DXPProduct, MIDProduct)
        products.push(insurance)
      }
    })

    return products
  }

  async function loadConfiguration(reset = false) {
    try {
      if (globalConfig.value?.businessLineId && !reset) {
        hiringAnswers.value.businessLine = globalConfig.value.businessLineId
        return
      }

      const endpoint = ENDPOINTS.DXP_GET_BRANCH_TRAVEL
      const { error, data } = await DXPService.get(endpoint)
      if (error) {
        throw new ServiceError(data).setDisplayType(ErrorDisplayTypeEnum.MODAL)
      }

      globalConfig.value = new TravelConfigurations(data)
      hiringAnswers.value.businessLine = globalConfig.value.businessLineId
    } catch (error) {
      loadingStore.stopLoading()
      errorManager.handleError(error)
    }
  }

  async function getNewRaterPrice(
    answers: TravelHiringAnswers,
    errorDisplayType: ErrorDisplayTypeEnum = ErrorDisplayTypeEnum.TOAST,
    isQuote: boolean = true,
    productCode: string | null | undefined = undefined
  ): Promise<TravelInsurance | null> {
    try {
      loadingStore.startLoading('common.loading.processing')

      const endpoint = ENDPOINTS.RATER_CALCULATE_PRICE_TRAVEL
      const request = TravelCommonMapper.GetPricingMapper.toRequest(
        answers,
        isQuote,
        commonStore.infoMediator?.id
      )
      const { error, data } = await MiddlewareService.post(endpoint, request)
      if (error) throw new ServiceError(data)

      const products = data.listPricing.map((l: RaterPricing) => {
        return new ProductId({
          productCode: l.productCode,
          clientCode: l.clientCode
        })
      })
      const dxpData = await _fetchDXPInsurances(products)
      const insurances = _loadCompleteSheetProducts(dxpData, data.listPricing)
      return (
        (productCode ? insurances.find((i) => i.productCode === productCode) : insurances[0]) ??
        null
      )
    } catch (error) {
      if (error instanceof CustomError) {
        error.setDisplayType(errorDisplayType)
      }
      errorManager.handleError(error)

      return null
    } finally {
      loadingStore.stopLoading()
    }
  }

  async function hireProduct(
    quotationId: string | null,
    startDate: Date,
    billingCycle: string,
    product: TravelInsurance
  ) {
    let valid = true

    try {
      loadingStore.startLoading('common.loading.processing')

      if (!quotationId) throw new Error('Quotation id is required')

      const endpoint = ENDPOINTS.SAVE_QUOTATION_TRAVEL
      const request = TravelMapper.SaveQuotationMapper.toRequest(
        quotationId,
        startDate,
        billingCycle,
        product,
        hiringAnswers.value.holder.role
      )
      const { error, data } = await MiddlewareService.post(endpoint, request)
      if (error) throw new ServiceError(data)
    } catch (error) {
      console.error(error)
      valid = false
    } finally {
      loadingStore.stopLoading()
    }

    return { valid }
  }

  async function fetchHiringFormAnswers(quotationId: string) {
    try {
      commonStore.showLogo = false
      const endpoint = `${ENDPOINTS.GET_RECOVERY_TRAVEL}?quotationId=${quotationId}`
      const { error, data } = await MiddlewareService.get(endpoint)
      if (error) {
        const modalButtons = [new ModalButton({ text: 'copy_button_emission_exit', navTo: 'exit' })]
        throw new ServiceError(data)
          .setDisplayType(ErrorDisplayTypeEnum.MODAL)
          .setButtons(modalButtons)
      }
      await commonStore.resolveMediatorFromRecovery(data.mediator?.mediator)
      await loadConfiguration(true)
      const branchConfig = getBranchConfiguration()
      const itsOk = await checkRecoveredQuotationBranch(
        data.hiring.businessLine,
        branchConfig.businessLineId,
        quotationId
      )
      if (!itsOk) return
      const { productCode, clientCode } = data.hiring
      const product = new ProductId({ productCode, clientCode })
      const dxpData = await _fetchDXPInsurances([product])
      if (!dxpData.length) {
        const modalButtons = [new ModalButton({ text: 'copy_button_emission_exit', navTo: 'exit' })]
        throw new ServiceError(data)
          .setDisplayType(ErrorDisplayTypeEnum.MODAL)
          .setButtons(modalButtons)
      }

      const DXPProduct = dxpData[0]
      const MIDProduct = data.hiring
      const insurance = TravelCommonMapper.GetProductsMapper.fromResponse(DXPProduct, MIDProduct)
      hiringAnswers.value = TravelMapper.GetRecoveryMapper.fromResponse(data, insurance)
    } catch (error) {
      errorManager.handleError(error)
      loadingStore.stopLoading()
    } finally {
      commonStore.showLogo = true
    }
  }

  const availableHiringMethods = {
    sendPolicyHolderDocument,
    checkExistClient,
    sendPolicyHolder,
    sendInsureds,
    goToPayment
  }
  async function callToHiringMethod(
    methodName: keyof typeof availableHiringMethods,
    answers: TravelHiringAnswers,
    errorDisplayType: ErrorDisplayTypeEnum
  ) {
    try {
      loadingStore.startLoading('common.loading.processing')

      if (methodName && methodName in availableHiringMethods) {
        await availableHiringMethods[methodName](answers)
      }
    } catch (error) {
      const err =
        error instanceof CustomError
          ? error.setDisplayType(
              error.displayType || errorDisplayType || ErrorDisplayTypeEnum.TOAST
            )
          : error
      errorManager.handleError(err)
      throw error
    }
  }
  function _setStepProcess(stepProcess: TravelInsuranceProcessStepEnum) {
    const currentStepCode = hiringAnswers.value.processStatus.stepCode ?? 0
    if (currentStepCode < stepProcess) {
      hiringAnswers.value.processStatus.stepCode = stepProcess
    }
  }
  async function sendPolicyHolderDocument(answers: TravelHiringAnswers) {
    if (answers.currentInsured?.role === UserRoleEnum.INSURED) return
    const endpoint = ENDPOINTS.SAVE_DOCUMENT_TRAVEL
    const quotationId = answers.quotationId ?? ''
    const holder = answers.holder
    const request = await TravelMapper.SaveDocumentMapper.toRequest(quotationId, holder)
    const { error, data } = await MiddlewareService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    _setStepProcess(TravelInsuranceProcessStepEnum.POLICY_HOLDER_SECOND)
    toastStore.successToast('health.saved-holder-data-and-document')
  }

  async function checkExistClient(answers: TravelHiringAnswers) {
    const { currentInsured } = answers
    if (currentInsured === null || currentInsured?.role === UserRoleEnum.HOLDER_NOT_INSURED) return
    const endpoint = ENDPOINTS.MDW_HEALTH_EXIST_CLIENT
    const request = TravelMapper.ExistClientMapper.toRequest(currentInsured)

    const { error, data } = await MiddlewareService.post(endpoint, request)
    if (error) throw new ServiceError(data)
    else if (!data.clientCorrectData && data.clientExists) {
      problemDataInfoModal()
      throw new ServiceError({
        message: 'error-first-problem',
        title: 'error-first-problem',
        code: '404',
        serviceName: endpoint,
        displayType: ErrorDisplayTypeEnum.WITHOUT_TOAST
      })
    }

    const thirdPartiesAnswered = data.hasDataPD && data.clientExists
    currentInsured.updateAttributes({ thirdPartiesAnswered })
    if (currentInsured?.role === UserRoleEnum.HOLDER_INSURED) {
      setGloballyAnswers({ holder: currentInsured })
    } else {
      setGloballyAnswers({ currentInsured })
    }
  }

  function problemDataInfoModal() {
    const modalMessage = new Message({
      title: t('common.error.data-error'),
      label: t('common.error.help-request'),
      buttons: [
        new ModalButton({
          text: 'copy_button_continue',
          callback: () => supportModalStore.setOpenModal(true)
        })
      ]
    })
    messageStore.supportModal(modalMessage)
  }

  async function sendPolicyHolder(answers: TravelHiringAnswers) {
    const endpoint = ENDPOINTS.SAVE_HOLDER_TRAVEL
    const quotationId = answers.quotationId ?? ''
    const holder = answers.holder
    const request = TravelMapper.SavePolicyHolderMapper.toRequest(quotationId, holder)
    const { error, data } = await MiddlewareService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    _setStepProcess(TravelInsuranceProcessStepEnum.INSUREDS)

    toastStore.successToast('health.saved-holder-data')
  }

  async function saveInsureds(answers: TravelHiringAnswers) {
    const endpoint = ENDPOINTS.SAVE_INSURED_TRAVEL
    const quotationId = answers.quotationId ?? ''

    const request = TravelCommonMapper.SaveTravelMapper.toRequest(quotationId, [
      answers.currentInsured!
    ])
    const { error, data } = await MiddlewareService.post(endpoint, request)
    if (error) throw new ServiceError(data)
  }

  async function sendInsureds(answers: TravelHiringAnswers) {
    if (answers.currentInsured?.role !== UserRoleEnum.INSURED) return
    await saveInsureds(answers)

    _setStepProcess(TravelInsuranceProcessStepEnum.PAYMENT)
    toastStore.successToast(
      t('travel.saved-insured-data', { riskNumber: answers.currentInsured?.riskNumber })
    )
  }

  async function goToPayment(answers: TravelHiringAnswers) {
    gtmStore.pageEvent(GTMPageType.CONTRATACION)
    loadingStore.stopLoading()
    loadingStore.hideHeader()
    loadingStore.startLoadingWithOptions({
      overLoaderTexts: [t('travel.go-to-payment')],
      icon: 'icon-padlock'
    })

    const request = CommonRequestsMapper.GetPaymentURLMapper.toRequest(
      answers.quotationId!,
      answers.paymentType
    )

    const endpoint = ENDPOINTS.GET_PAYMENT_URL
    const { error, data } = await MiddlewareService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    const response = new GetPaymentUrlResponse(data)
    window.location.href = response.urlPayment
  }

  function isEmitted() {
    const stepCode = hiringAnswers.value?.processStatus?.stepCode
    if (!stepCode) throw new Error('Processs step code is required')

    return stepCode === TravelInsuranceProcessStepEnum.EMISSION
  }

  async function emission(quotationId: string) {
    return await commonStore.fetchInsuranceState(
      quotationId,
      'hiring-travel',
      ENDPOINTS.POLICY_EMISSION_TRAVEL
    )
  }

  function isProcessExpired(): boolean {
    const statusCode = hiringAnswers.value.processStatus.statusCode
    return statusCode === InsuranceProcessStatusEnum.EXPIRED
  }

  function getSummary(): Summary {
    return new TravelHiringSummary(hiringAnswers.value as TravelHiringAnswers, t)
  }

  function isFinished() {
    const stepCode = hiringAnswers.value?.processStatus?.stepCode
    const statusCode = hiringAnswers.value?.processStatus?.statusCode
    if (!stepCode) throw new Error('Processs step code is required')

    return (
      stepCode >= TravelInsuranceProcessStepEnum.EMISSION &&
      statusCode === InsuranceProcessStatusEnum.EMITTED
    )
  }

  async function _repriceSeveralTravel() {
    const insurance = await getNewRaterPrice(
      new TravelHiringAnswers(hiringAnswers.value),
      ErrorDisplayTypeEnum.TOAST,
      true,
      hiringAnswers.value.product!.productCode
    )
    if (
      !insurance ||
      insurance.prices[hiringAnswers.value.billingCycle as keyof typeof insurance.prices] !==
        hiringAnswers.value.price
    ) {
      return 'Step0_1_StartDateForm'
    }
    await hireProduct(
      hiringAnswers.value.quotationId,
      hiringAnswers.value.startDate,
      hiringAnswers.value.billingCycle as string,
      insurance
    )
  }

  async function getFirstPage(pages: Record<string, FormPage>) {
    if (isProcessExpired()) {
      if (
        String(hiringAnswers.value.insuranceType) !== globalConfig.value.insuranceTypes.several.id
      ) {
        return 'Step0_1_StartDateForm'
      }
      const today = new Date()
      const oneYear = new Date()
      oneYear.setFullYear(oneYear.getFullYear() + 1)
      setGloballyAnswers({
        startDate: today,
        departureDate: today,
        returnDate: oneYear,
        processStatus: new ProcessStatus({ ...hiringAnswers.value.processStatus, statusCode: 1 })
      })
      await _repriceSeveralTravel()
    }

    if (isEmitted()) {
      return 'Step2_1_PaymentType'
    }

    const insureds = hiringAnswers.value.insureds as Insured[]
    const processStep = hiringAnswers.value.processStatus?.stepCode ?? null

    return navigation.getPageByStepProcess(
      pages,
      processStep,
      insureds,
      TravelInsuranceProcessStepEnum.INSUREDS
    )
  }

  function loadPages() {
    const expectedPages: Record<string, FormPage> = {}

    const holder = hiringAnswers.value.holder as Insured
    const insureds = hiringAnswers.value.insureds as Insured[]
    const insuredsToFill = pagesBuilder.getPageInsureds(holder, insureds)

    const step1Pages = pagesBuilder.getDynamicPages(PAGES.STEP1_PAGES, insuredsToFill)
    const step2Pages = PAGES.STEP2_PAGES

    // Join step1 with step2
    if (Object.keys(step1Pages).length) {
      const step1LastPage = pagesBuilder.getLastPageName(step1Pages)
      const step2FirstPage = pagesBuilder.getFirstPageName(step2Pages)
      step1Pages[step1LastPage].next[0] = step2FirstPage
    }

    if (isProcessExpired()) {
      const step0pages = PAGES.STEP0_PAGES
      const step0LastPage = pagesBuilder.getLastPageName(step0pages)
      const step1FirstPage = pagesBuilder.getFirstPageName(step1Pages)
      step0pages[step0LastPage].next[0] = step1FirstPage
      Object.assign(expectedPages, step0pages)
    }

    Object.assign(expectedPages, step1Pages)
    Object.assign(expectedPages, step2Pages)

    return expectedPages
  }

  function getBranchName(): string {
    return 'travel'
  }

  function getRePricingPages() {
    return PAGES.STEP0_PAGES
  }

  function checkIfNeedsRecalculate() {
    if (
      String(hiringAnswers.value.insuranceType) === globalConfig.value.insuranceTypes.several.id
    ) {
      hiringAnswers.value.departureDate.setHours(0, 0, 0, 0)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return hiringAnswers.value.departureDate < today
    }
    return false
  }

  function hasFinishedInsuredSteps(processStep: TravelInsuranceProcessStepEnum) {
    return processStep >= TravelInsuranceProcessStepEnum.INSUREDS
  }

  return {
    getStepsBeforeSaveAndExit,

    getBranchConfiguration,
    loadConfiguration,
    isFinished,
    setCurrentInsuredAnswers,
    setGloballyAnswers,
    getHiringAnswers,
    loadPages,
    getNewRaterPrice,
    getRePricingPages,
    hireProduct,
    getSummary,
    isProcessExpired,
    fetchHiringFormAnswers,
    callToHiringMethod,
    emission,
    isEmitted,
    getFirstPage,
    getBranchName,
    checkIfNeedsRecalculate,
    hasFinishedInsuredSteps
  }
})
